import "./ReviewTab.css";
import React from "react";
import { RpgRules } from "../../../Services/RpgRulesService";
import { IRpgCharacter } from "../../../models/IRpgCharacter";
import { RpgItemType } from "../../../models/enums";
import { getCharacterPortrait, getCoinQty } from "../characterHelpers";
import { WorkshopMode } from "../CharacterWorkshop";
import UiButton from "../../../components/Buttons/UiButton";
import { AbilityScore } from "../../../components/Character/AbilityStats/AbilityScore";
import { IRpgClass } from "../../../models/IRpgClass";
import { IRpgRace } from "../../../models/IRpgRace";
import { CharacterAvatar } from "../../../components/Character/CharacterImage/CharacterAvatar";

interface IReviewTabProps {
    isReadOnly: boolean;
    tempCharacter: IRpgCharacter;
    reviewIssues: string[];
    canSave: boolean;
    mode: WorkshopMode;
    hasSaved: boolean;
    selectedRace: IRpgRace | null;
    selectedClass: IRpgClass | null;
    onSaveCharacter: () => void;
}

const ReviewTab: React.FC<IReviewTabProps> = ({
    isReadOnly, 
    tempCharacter, 
    reviewIssues, 
    canSave, 
    mode, 
    hasSaved, 
    selectedRace, 
    selectedClass, 
    onSaveCharacter, 
}) => {
    const portraitUrl: string = getCharacterPortrait(tempCharacter);
    const shouldShowSaveButton = mode === "create" || (mode === "edit" && !hasSaved);
    const inventoryItems = (tempCharacter.Items ?? [])
        .filter((item) => (item.Quantity ?? 0) > 0 && item.Type !== RpgItemType.Coin)
        .sort((a, b) => (a.Type - b.Type) || (a.Name ?? "").localeCompare(b.Name ?? ""));

    const raceBonusLines: string[] = [];
    const raceBonuses = selectedRace?.Bonuses;

    if (raceBonuses) {
        (raceBonuses.Traits ?? []).forEach((trait) => raceBonusLines.push(trait));
        (raceBonuses.Languages ?? []).forEach((lang) => raceBonusLines.push(`Language: ${lang}`));
        (raceBonuses.Proficiencies ?? []).forEach((prof) => raceBonusLines.push(`Proficiency: ${prof}`));
    }

    const classFeatureLines: string[] = (selectedClass?.Traits ?? []).map((t) => {
        const name = t.Name ?? "";
        const desc = t.Description ?? "";
        return desc ? `${name}: ${desc}` : name;
    }).filter((x) => x.trim().length > 0);

    const keySkillLines: string[] = (selectedClass?.KeySkills ?? []).filter((x) => (x ?? "").trim().length > 0);

    const getFieldText = (value: unknown): string => {
        if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")) {
            return "(not set)";
        }
        return value as string;
    };

    const onPrintSheet = (): void => {
        window.print();
    };

    const onExportPdf = (): void => {
        // Placeholder until PDF export is implemented.
        alert("Export as PDF is not implemented yet.");
    };

    return (
        <div className="reviewTab">
            {/* Validation / issues */}
            {reviewIssues.length > 0 && (
                <div className="reviewIssues">
                    <div className="reviewIssues__title">Fix these before saving:</div>
                    <ul className="reviewList">
                        {reviewIssues.map((issue) => (
                            <li key={issue}>{issue}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="reviewSheet">
                <div className="reviewBanner">
                    <h2 className="reviewBanner__title">
                        Character Sheet Summary:{" "}
                        <span className="reviewBanner__titleName">{getFieldText(tempCharacter.Name)}</span>
                    </h2>
                </div>

                <div className="reviewGrid">
                    {/* Left column */}
                    <div className="reviewCol reviewCol--left">
                        <section className="reviewSection reviewSection--vitals">
                            <div className="reviewSection__header">
                                <h3 className="reviewSection__title">Vitals & Combat</h3>
                                <div className="reviewSection__rule" />
                            </div>
                            <div className="reviewSection__body">
                                <div className="reviewFieldGrid">
                                    <div className="reviewFieldGrid__label">Armor Class</div>
                                    <div className="reviewFieldGrid__value">{tempCharacter.ArmorClass}</div>

                                    <div className="reviewFieldGrid__label">Hit Points</div>
                                    <div className="reviewFieldGrid__value">
                                        {tempCharacter.HitPoints} / {tempCharacter.HitPointsMax}
                                    </div>

                                    <div className="reviewFieldGrid__label">Initiative</div>
                                    <div className="reviewFieldGrid__value">{RpgRules.calcInitive(tempCharacter)}</div>

                                    <div className="reviewFieldGrid__label">Speed</div>
                                    <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Speed)}</div>
                                </div>
                            </div>
                        </section>

                        <section className="reviewSection reviewSection--abilities">
                            <div className="reviewSection__header">
                                <h3 className="reviewSection__title">Ability Scores</h3>
                                <div className="reviewSection__rule" />
                            </div>
                                <div className="reviewSection__body">
                                    <div className="reviewAbilitiesGrid">
                                        {(tempCharacter.AbilityScores ?? []).map((stat) => (
                                            <AbilityScore
                                                key={stat.Name}
                                                stat={stat}
                                                size="sm"
                                            />
                                        ))}
                                    </div>
                                </div>
                        </section>
                    </div>

                    {/* Middle column */}
                    <div className="reviewCol reviewCol--mid">
                        <section className="reviewSection reviewSection--appearance">
                            <div className="reviewSection__header">
                                <h3 className="reviewSection__title">Appearance & Background</h3>
                                <div className="reviewSection__rule" />
                            </div>
                            <div className="reviewSection__body">
                                <div className="reviewAppearanceRow">
                                    <div className="reviewAppearanceAvatar">
                                        <CharacterAvatar
                                            size="lg"
                                            status="normal"
                                            name={getFieldText(tempCharacter.Name)}
                                            level={tempCharacter.Level || 1}
                                            imageUrl={portraitUrl}
                                        />
                                    </div>

                                    <div className="reviewAppearanceStats">
                                        <div className="reviewFieldGrid reviewFieldGrid--appearance">
                                            <div className="reviewFieldGrid__label">Gender</div>
                                            <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Appearance?.Gender)}</div>

                                            <div className="reviewFieldGrid__label">Height</div>
                                            <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Appearance?.Height)}</div>

                                            <div className="reviewFieldGrid__label">Weight</div>
                                            <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Appearance?.Weight)}</div>

                                            <div className="reviewFieldGrid__label">Eyes</div>
                                            <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Appearance?.EyeColor)}</div>

                                            <div className="reviewFieldGrid__label">Hair</div>
                                            <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Appearance?.HairColor)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="reviewSection reviewSection--core">
                            <div className="reviewSection__header">
                                <h3 className="reviewSection__title">Core Information</h3>
                                <div className="reviewSection__rule" />
                            </div>
                            <div className="reviewSection__body">
                                <div className="reviewFieldGrid">
                                    <div className="reviewFieldGrid__label">Name</div>
                                    <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Name)}</div>

                                    <div className="reviewFieldGrid__label">Alignment</div>
                                    <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Alignment)}</div>

                                    <div className="reviewFieldGrid__label">Race</div>
                                    <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Race)}</div>

                                    <div className="reviewFieldGrid__label">Class</div>
                                    <div className="reviewFieldGrid__value">{getFieldText(tempCharacter.Class)}</div>

                                    <div className="reviewFieldGrid__label">Background</div>
                                    <div className="reviewFieldGrid__value">{(getFieldText(tempCharacter.Background) === "(not set)") ? "None" : getFieldText(tempCharacter.Background)}</div>

                                    <div className="reviewFieldGrid__label">Level</div>
                                    <div className="reviewFieldGrid__value">{tempCharacter.Level || 0}</div>

                                    <div className="reviewFieldGrid__label">XP</div>
                                    <div className="reviewFieldGrid__value">{tempCharacter.Xp ?? 0}</div>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Right column */}
                    <div className="reviewCol reviewCol--right">
                        <section className="reviewSection reviewSection--features">
                            <div className="reviewSection__header">
                                <h3 className="reviewSection__title">Spells & Features</h3>
                                <div className="reviewSection__rule" />
                            </div>
                            <div className="reviewSection__body">
                                <div className="reviewScrollList">
                                    <div className="reviewList__group">Race Bonuses</div>
                                    <ul className="reviewList">
                                        {raceBonusLines.length === 0 ? (
                                            <li>(none)</li>
                                        ) : (
                                            raceBonusLines.map((line) => <li key={line}>{line}</li>)
                                        )}
                                    </ul>

                                    <div className="reviewList__group">Class Features</div>
                                    <ul className="reviewList">
                                        {classFeatureLines.length === 0 ? (
                                            <li>(none)</li>
                                        ) : (
                                            classFeatureLines.map((line) => <li key={line}>{line}</li>)
                                        )}
                                    </ul>

                                    <div className="reviewList__group">Key Skills</div>
                                    <ul className="reviewList">
                                        {keySkillLines.length === 0 ? (
                                            <li>(none)</li>
                                        ) : (
                                            keySkillLines.map((line) => <li key={line}>{line}</li>)
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </section>

                        <section className="reviewSection reviewSection--equipment">
                            <div className="reviewSection__header">
                                <h3 className="reviewSection__title">Equipment</h3>
                                <div className="reviewSection__rule" />
                            </div>
                            <div className="reviewSection__body">
                                <div className="reviewCoinsRow">
                                    <div><b>PP:</b> {getCoinQty(tempCharacter, "PP")}</div>
                                    <div><b>EP:</b> {getCoinQty(tempCharacter, "EP")}</div>
                                    <div><b>GP:</b> {getCoinQty(tempCharacter, "GP")}</div>
                                    <div><b>SP:</b> {getCoinQty(tempCharacter, "SP")}</div>
                                    <div><b>CP:</b> {getCoinQty(tempCharacter, "CP")}</div>
                                </div>

                                <div className="reviewScrollList reviewScrollList--tall">
                                    <ul className="reviewList">
                                        {inventoryItems.length === 0 ? (
                                            <li>You currently have no Items.</li>
                                        ) : (
                                            inventoryItems.map((item) => (
                                                <li key={`${item.Id}-${item.Name}`}>
                                                    <b>{item.Name || `(Item #${item.Id})`}</b> × {item.Quantity}{" "}
                                                    <span style={{ opacity: 0.85 }}>
                                                        ({RpgItemType[item.Type] ?? item.Type})
                                                    </span>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>

                <div className="reviewActions">
                    {shouldShowSaveButton ? (
                        <UiButton
                            variant="sage"
                            text="Save"
                            disabled={!canSave}
                            onClickCallback={onSaveCharacter}
                        />
                    ) : (
                        <>
                            <UiButton
                                variant="blue"
                                text="Print Sheet"
                                disabled={false}
                                onClickCallback={onPrintSheet}
                            />
                            <UiButton
                                variant="rust"
                                text="Export as PDF"
                                disabled={false}
                                onClickCallback={onExportPdf}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReviewTab;
